import { useCallback } from "react";
import {
  faChevronDown,
  faChevronRight,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Card } from "react-bootstrap";
import {
  HierarchyFilterResult,
  HierarchyNode,
} from "../../utils/hierarchyTree";
import "./HierarchyTreeView.css";

type HierarchyTreeViewLabels = {
  open: string;
  close: string;
  details: string;
  root: string;
  level: string;
  subItems: string;
  leaf: string;
  identifier: string;
  parentIdentifier: string;
  rootValue?: string;
};

type HierarchyTreeViewProps<T> = {
  filteredTree: HierarchyFilterResult<T>;
  collapsedNodes: Record<string, boolean>;
  normalizedSearchTerm: string;
  onToggleNode: (key: string) => void;
  onGoToDetails: (itemId?: string | null) => void;
  getKey: (item: T) => string;
  getDisplayName: (item: T) => string;
  labels: HierarchyTreeViewLabels;
};

function HierarchyTreeView<T extends object>({
  filteredTree,
  collapsedNodes,
  normalizedSearchTerm,
  onToggleNode,
  onGoToDetails,
  getKey,
  getDisplayName,
  labels,
}: HierarchyTreeViewProps<T>) {
  const renderNode = useCallback(
    (node: HierarchyNode<T>, depth: number): JSX.Element => {
      const key = getKey(node) || getDisplayName(node);
      const isSearchExpanded = filteredTree.expandedKeys.has(key);
      const isSearchMatch = filteredTree.matchKeys.has(key);
      const isCollapsed = normalizedSearchTerm
        ? (collapsedNodes[key] ?? !isSearchExpanded)
        : collapsedNodes[key] ?? true;
      const hasChildren = node.children.length > 0;
      const childCount = node.children.length;

      const parentKey = (node as any)?.partOf?.identifier?.value?.trim?.() ??
        (node as any)?.partOf?.reference?.split?.("/").pop?.() ??
        (node as any)?.partOf?.display?.trim?.() ??
        "";

      return (
        <div className="hierarchy-tree-node" key={`${key}-${depth}`}>
          <Card className={`hierarchy-tree-card ${isSearchMatch ? "is-search-match" : ""}`}>
            <Card.Header className="hierarchy-tree-card-header">
              <div className="hierarchy-tree-header-content">
                <div className="hierarchy-tree-header-left">
                  {hasChildren && (
                    <button
                      type="button"
                      className="hierarchy-tree-toggle"
                      onClick={() => onToggleNode(key)}
                      aria-label={isCollapsed ? labels.open : labels.close}
                    >
                      <FontAwesomeIcon
                        icon={isCollapsed ? faChevronRight : faChevronDown}
                      />
                    </button>
                  )}
                  <div className="hierarchy-tree-header-title">
                    <div className="hierarchy-tree-title-content">
                      <span className="hierarchy-tree-name">{getDisplayName(node)}</span>
                    </div>
                  </div>
                </div>
                <div className="hierarchy-tree-header-actions">
                  <Badge bg="secondary">
                    {depth === 0 ? labels.root : `${labels.level} ${depth + 1}`}
                  </Badge>
                  <Badge bg={hasChildren ? "primary" : "success"}>
                    {hasChildren
                      ? `${childCount} ${labels.subItems}`
                      : labels.leaf}
                  </Badge>
                  <button
                    type="button"
                    className="hierarchy-tree-details"
                    onClick={() => onGoToDetails((node as any)?.id ?? key)}
                  >
                    <FontAwesomeIcon icon={faEye} />
                    <span>{labels.details}</span>
                  </button>
                </div>
              </div>
            </Card.Header>
            {!isCollapsed && (
              <Card.Body>
                <div className="hierarchy-tree-meta">
                  <span>
                    <strong>{labels.identifier}:</strong> {(node as any)?.id || key}
                  </span>
                  <span>
                    <strong>{labels.parentIdentifier}:</strong>{" "}
                    {parentKey || labels.rootValue || "ROOT"}
                  </span>
                </div>
              </Card.Body>
            )}
          </Card>
          {!isCollapsed && hasChildren && (
            <div className="hierarchy-tree-children">
              {node.children.map((child) => renderNode(child, depth + 1))}
            </div>
          )}
        </div>
      );
    },
    [
      collapsedNodes,
      filteredTree.expandedKeys,
      filteredTree.matchKeys,
      getDisplayName,
      getKey,
      labels.close,
      labels.details,
      labels.identifier,
      labels.leaf,
      labels.level,
      labels.open,
      labels.parentIdentifier,
      labels.root,
      labels.rootValue,
      labels.subItems,
      normalizedSearchTerm,
      onGoToDetails,
      onToggleNode,
    ]
  );

  return (
    <div className="hierarchy-tree">{filteredTree.nodes.map((node) => renderNode(node, 0))}</div>
  );
}

export default HierarchyTreeView;
